import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import StreamerInfoTabs from '../StreamerInfoTabs';
import HandleNameSettingModal from '../HandleNameSettingModal';
import Banner from '../Banner';
import EventNotificationButton from '../EventNotificationButton';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../../contexts/AuthContext';

interface BeforeEntryViewProps {
    streamerName: string;
    profileImageUrl: string | null;
    isOnline: boolean;
    isSuspended?: boolean;  // Whether broadcast is temporarily suspended
    streamKey: string | null;
    streamerId: string;
    streamerDbId: number | null;  // Database ID for favorites
    scheduleId?: string | null;  // Event schedule ID for notifications
    onEnterLiveRoom: () => void;
    onEnterPeekRoom: () => void;
    username?: string;
    handleName?: string | null;
    onHandleNameUpdate?: (newHandleName: string) => void;
    isAuthenticated?: boolean;  // Whether user is logged in
    partyCount?: number;  // Number of viewers in chat room
    peekCount?: number;   // Number of viewers in peek room
}

type NotificationMessage = {
    type: 'success' | 'error';
    key:
        | 'eventNotification.unsubscribeSuccess'
        | 'eventNotification.subscribeSuccess'
        | 'eventNotification.unsubscribeError'
        | 'eventNotification.subscribeError';
    defaultValue: string;
};

export default function BeforeEntryView({
    streamerName,
    profileImageUrl,
    isOnline,
    isSuspended = false,
    streamKey: _streamKey, // Reserved for future use
    streamerId,
    streamerDbId,
    scheduleId,
    onEnterLiveRoom,
    onEnterPeekRoom,
    username = '',
    handleName,
    onHandleNameUpdate,
    isAuthenticated = true,
    partyCount = 0,
    peekCount = 0,
}: BeforeEntryViewProps) {
    const { t } = useTranslation('watch');
    const { t: tCommon } = useTranslation('common');
    const { isAdminOrManager, isViewer } = useAuth();
    const canReport = isViewer?.() ?? false;
    const [isSettingModalOpen, setIsSettingModalOpen] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);
    const [favoriteMessage, setFavoriteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Event notification state (centralized)
    const [isNotificationSubscribed, setIsNotificationSubscribed] = useState(false);
    const [isNotificationSent, setIsNotificationSent] = useState(false);
    const [notificationLoading, setNotificationLoading] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState<NotificationMessage | null>(null);

    // Check favorite status when component mounts (only for authenticated users)
    useEffect(() => {
        if (!streamerDbId || !isAuthenticated) return;

        const checkFavoriteStatus = async () => {
            try {
                const response = await apiClient.checkFavoriteStatus(streamerDbId);
                setIsFavorite(response.is_favorite);
            } catch (error) {
                console.error('Failed to check favorite status:', error);
            }
        };

        checkFavoriteStatus();
    }, [streamerDbId, isAuthenticated]);

    // Check event notification status
    const checkNotificationStatus = useCallback(async () => {
        if (!isAuthenticated || !scheduleId || isAdminOrManager()) return;

        try {
            const status = await apiClient.checkEventNotificationStatus(scheduleId);
            setIsNotificationSubscribed(status.subscribed);
            setIsNotificationSent(status.notification_sent || false);
        } catch (err) {
            console.error('Failed to check event notification status:', err);
        }
    }, [scheduleId, isAuthenticated, isAdminOrManager]);

    useEffect(() => {
        checkNotificationStatus();
    }, [checkNotificationStatus]);

    // Toggle event notification subscription
    const handleToggleNotification = useCallback(async () => {
        if (!isAuthenticated || !scheduleId || notificationLoading) return;

        setNotificationLoading(true);
        setNotificationMessage(null);

        try {
            if (isNotificationSubscribed) {
                await apiClient.unsubscribeEventNotification(scheduleId);
                setIsNotificationSubscribed(false);
                setIsNotificationSent(false);
                setNotificationMessage({
                    type: 'success',
                    key: 'eventNotification.unsubscribeSuccess',
                    defaultValue: '通知を解除しました',
                });
            } else {
                await apiClient.subscribeEventNotification(scheduleId);
                // Re-fetch status to get notification_sent state (for immediate notifications within 30min)
                const status = await apiClient.checkEventNotificationStatus(scheduleId);
                setIsNotificationSubscribed(status.subscribed);
                setIsNotificationSent(status.notification_sent || false);
                setNotificationMessage({
                    type: 'success',
                    key: 'eventNotification.subscribeSuccess',
                    defaultValue: '通知を登録しました',
                });
            }
            setTimeout(() => setNotificationMessage(null), 3000);
        } catch (err) {
            console.error('Failed to toggle event notification:', err);
            setNotificationMessage({
                type: 'error',
                key: isNotificationSubscribed
                    ? 'eventNotification.unsubscribeError'
                    : 'eventNotification.subscribeError',
                defaultValue: isNotificationSubscribed
                    ? '通知解除に失敗しました'
                    : '通知登録に失敗しました',
            });
        } finally {
            setNotificationLoading(false);
        }
    }, [isAuthenticated, scheduleId, notificationLoading, isNotificationSubscribed]);

    const displayedNotificationMessage = notificationMessage && {
        type: notificationMessage.type,
        text: tCommon(notificationMessage.key, notificationMessage.defaultValue),
    };

    const handleToggleFavorite = async () => {
        if (!streamerDbId || favoriteLoading) return;

        setFavoriteLoading(true);
        setFavoriteMessage(null);
        try {
            if (isFavorite) {
                await apiClient.removeFavorite(streamerDbId);
                setIsFavorite(false);
                setFavoriteMessage({ type: 'success', text: t('beforeEntry.favoriteRemoved') });
            } else {
                await apiClient.addFavorite(streamerDbId);
                setIsFavorite(true);
                setFavoriteMessage({ type: 'success', text: t('beforeEntry.favoriteAdded') });
            }
            // 3秒後にメッセージを消す
            setTimeout(() => setFavoriteMessage(null), 3000);
        } catch (error) {
            console.error('Failed to toggle favorite:', error);
            setFavoriteMessage({ type: 'error', text: t('beforeEntry.favoriteError') });
            setTimeout(() => setFavoriteMessage(null), 5000);
        } finally {
            setFavoriteLoading(false);
        }
    };

    return (
        <>
            {/* Video and Right Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 mb-4">
                {/* Video Player */}
                <div className="bg-black overflow-hidden">
                    {/* Room Status Bar */}
                    <div className="bg-black text-white flex items-stretch justify-between pr-4 text-sm">
                        <div className={`${isOnline ? 'bg-pink-500' : isSuspended ? 'bg-yellow-500' : 'bg-gray-500'} text-white px-3 py-2 font-semibold flex items-center`}>
                            {isOnline ? t('beforeEntry.online') : isSuspended ? t('beforeEntry.suspended') : t('beforeEntry.offline')}
                        </div>
                        <div className="flex gap-6 text-white items-center">
                            <div className="flex items-center gap-2">
                                <span>💬</span>
                                <span>{t('beforeEntry.chatting')}: <strong>{t('beforeEntry.people', { count: partyCount })}</strong></span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span>👀</span>
                                <span>{t('beforeEntry.peeking')}: <strong>{t('beforeEntry.people', { count: peekCount })}</strong></span>
                            </div>
                        </div>
                    </div>

                    {/* Video Player */}
                    <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                        {profileImageUrl ? (
                            <>
                                <img
                                    src={profileImageUrl}
                                    alt={streamerName}
                                    className="w-full h-full object-cover blur-sm"
                                />
                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                    <div className="w-[300px] h-[300px] overflow-hidden shadow-lg">
                                        <img
                                            src={profileImageUrl}
                                            alt={streamerName}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center">
                                <div className="text-6xl mb-4">📺</div>
                                <div className="text-gray-600 text-lg">{streamerName}</div>
                            </div>
                        )}

                    </div>
                </div>

                {/* Right Sidebar */}
                <div className="flex flex-col gap-4">
                    {/* Banner Area */}
                    <Banner
                        location="room_header"
                        className="bg-white overflow-hidden"
                        imgClassName="w-full h-auto"
                        showEditButton={isAdminOrManager()}
                    />

                    {/* Action Buttons */}
                    <div className="bg-white border border-gray-200 p-4 flex flex-col gap-3 flex-1">
                        {!isOnline && !isSuspended && (
                            <div className="text-red-600 text-base font-bold text-center py-4">
                                {t('beforeEntry.offlineMessage')}
                            </div>
                        )}
                        {isSuspended && (
                            <div className="text-yellow-600 text-base font-bold text-center py-4">
                                <div className="text-2xl mb-2">⏳</div>
                                {t('beforeEntry.suspendedMessage')}
                            </div>
                        )}

                        {/* Settings Button - Only for authenticated viewers (not admin/manager) */}
                        {isAuthenticated && !isAdminOrManager() && (
                            <button
                                onClick={() => setIsSettingModalOpen(true)}
                                className="w-full py-3 px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-800 transition-colors flex items-center justify-center gap-2 font-semibold text-sm"
                            >
                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                {t('beforeEntry.setNameBefore')}
                            </button>
                        )}

                        {/* Favorite Button - Only for authenticated viewers (not admin/manager) */}
                        {isAuthenticated && !isAdminOrManager() && streamerDbId && (
                            <div>
                                <button
                                    onClick={handleToggleFavorite}
                                    disabled={favoriteLoading}
                                    className={`w-full py-3 px-4 transition-colors flex items-center justify-center gap-2 font-semibold text-sm ${
                                        isFavorite
                                            ? 'bg-pink-500 hover:bg-pink-600 text-white border border-pink-500'
                                            : 'bg-white hover:bg-pink-50 border border-pink-300 text-pink-500'
                                    } ${favoriteLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                                    </svg>
                                    {favoriteLoading ? t('beforeEntry.processing') : isFavorite ? t('beforeEntry.favoriteRegistered') : t('beforeEntry.addToFavorite')}
                                </button>
                                {favoriteMessage && (
                                    <div className={`mt-2 px-3 py-2 text-sm text-center ${
                                        favoriteMessage.type === 'success'
                                            ? 'bg-green-50 text-green-700 border border-green-200'
                                            : 'bg-red-50 text-red-700 border border-red-200'
                                    }`}>
                                        {favoriteMessage.text}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Event Notification Button - Only for viewers with scheduled events (not live) */}
                        {scheduleId && !isOnline && !isSuspended && isAuthenticated && !isAdminOrManager() && (
                            <div className="flex justify-center">
                                <EventNotificationButton
                                    isSubscribed={isNotificationSubscribed}
                                    isLoading={notificationLoading}
                                    onToggle={handleToggleNotification}
                                    message={displayedNotificationMessage}
                                    className="w-full"
                                />
                            </div>
                        )}

                        {/* Room Entry Buttons */}
                        <div className="mt-auto flex flex-col gap-3">
                            <button
                                onClick={onEnterLiveRoom}
                                disabled={!isOnline}
                                className={`w-full py-4 px-4 transition-all flex items-center justify-center gap-2 font-semibold text-base ${isOnline
                                    ? 'bg-pink-500 hover:bg-pink-600 text-white hover:shadow-lg hover:-translate-y-0.5'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                    }`}
                            >
                                <svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                                    <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                                </svg>
                                {t('beforeEntry.chatRoom')}
                            </button>
                            <button
                                onClick={onEnterPeekRoom}
                                disabled={!isOnline}
                                className={`w-full py-4 px-4 transition-all flex items-center justify-center gap-2 font-semibold text-base ${isOnline
                                    ? 'bg-pink-500 hover:bg-pink-600 text-white hover:shadow-lg hover:-translate-y-0.5'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                    }`}
                            >
                                <svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                                {t('beforeEntry.peekRoom')}
                            </button>
                            <p className="text-center text-xs text-gray-600">{t('beforeEntry.roomCost')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Information Tabs - Only for viewers (not admin/manager) */}
            {!isAdminOrManager() && (
                <div className="bg-white border border-gray-200 p-6 shadow-sm">
                    <StreamerInfoTabs
                        username={streamerId}
                        scheduleId={scheduleId}
                        isLoggedIn={isAuthenticated}
                        canReport={canReport}
                        isNotificationSubscribed={isNotificationSubscribed}
                        isNotificationSent={isNotificationSent}
                        notificationLoading={notificationLoading}
                        notificationMessage={displayedNotificationMessage}
                        onToggleNotification={handleToggleNotification}
                    />
                </div>
            )}

            {/* Handle Name Setting Modal */}
            <HandleNameSettingModal
                isOpen={isSettingModalOpen}
                onClose={() => setIsSettingModalOpen(false)}
                username={username}
                handleName={handleName}
                onHandleNameUpdate={onHandleNameUpdate}
            />
        </>
    );
}
